/**
 * Per-context category vocabulary registry.
 *
 * Entry ids are `{app}.{namespace}:{value}` (e.g. `uvilo-ai.factoid-category:coaching`).
 * App slot maps are append-only: changing a slot re-interprets existing Factoid rows.
 */

export type VocabularyEntry = {
  readonly id: string;
  readonly app: string;
  readonly namespace: string;
  readonly value: string;
};

/** Namespaces bound to Factoid.category1 / category2 for a given app. */
export type VocabularyAppSlotMap = {
  readonly category1: string;
  readonly category2: string;
};

const vocabularyEntries: VocabularyEntry[] = [];
const vocabularyById = new Map<string, VocabularyEntry>();
const vocabularyByAppNamespace = new Map<string, VocabularyEntry[]>();

/** Append-only map of app → category slot namespaces. */
export const VOCABULARY_APP_SLOT_MAPS: Record<string, VocabularyAppSlotMap> = {
  'uvilo-ai': {
    category1: 'factoid-category',
    category2: 'life-domain',
  },
};

/**
 * Builds a vocabulary entry id from its parts.
 * @param app Owning application (`uvilo-ai` or `forge`)
 * @param namespace Vocabulary namespace (e.g. `factoid-category`)
 * @param value Category value (e.g. `coaching`)
 * @returns Registry id `app.namespace:value`
 */
export function vocabularyEntryId(app: string, namespace: string, value: string): string {
  return `${app}.${namespace}:${value}`;
}

/**
 * Registers a vocabulary entry. Replaces an existing entry with the same id (HMR-safe).
 * @param entry Vocabulary entry to register
 */
export function registerVocabularyEntry(entry: VocabularyEntry): void {
  const existingIndex = vocabularyEntries.findIndex((e) => e.id === entry.id);
  if (existingIndex >= 0) {
    vocabularyEntries[existingIndex] = entry;
  } else {
    vocabularyEntries.push(entry);
  }
  vocabularyById.set(entry.id, entry);

  const key = `${entry.app}\0${entry.namespace}`;
  const list = vocabularyByAppNamespace.get(key) ?? [];
  const listIndex = list.findIndex((e) => e.id === entry.id);
  if (listIndex >= 0) {
    list[listIndex] = entry;
  } else {
    list.push(entry);
  }
  vocabularyByAppNamespace.set(key, list);
}

/**
 * Registers every value in `values` for the given app and namespace.
 * Prefer this over listing individual `registerVocabularyEntry` calls.
 * @param app Owning application (`uvilo-ai` or `forge`)
 * @param namespace Vocabulary namespace (e.g. `factoid-category`)
 * @param values Category values to register (typically from an enum via `Object.values`)
 */
export function registerVocabularyValues(
  app: string,
  namespace: string,
  values: readonly string[],
): void {
  for (const value of values) {
    registerVocabularyEntry({
      id: vocabularyEntryId(app, namespace, value),
      app,
      namespace,
      value,
    });
  }
}

/**
 * Looks up a vocabulary entry by id.
 * @param id Entry id (`app.namespace:value`)
 * @returns The entry, or undefined if unregistered
 */
export function getVocabularyEntry(id: string): VocabularyEntry | undefined {
  return vocabularyById.get(id);
}

/**
 * Returns all registered values for an app + namespace.
 * @param app Owning application (`uvilo-ai` or `forge`)
 * @param namespace Vocabulary namespace (e.g. `factoid-category`)
 * @returns Registered values (order of registration)
 */
export function getVocabularyValues(app: string, namespace: string): readonly string[] {
  const key = `${app}\0${namespace}`;
  return (vocabularyByAppNamespace.get(key) ?? []).map((entry) => entry.value);
}

/**
 * Returns whether a value is registered for the app's given namespace.
 * @param app Owning application (`uvilo-ai` or `forge`)
 * @param namespace Vocabulary namespace (e.g. `factoid-category`)
 * @param value Category value to check
 * @returns True when the value is registered
 */
export function isVocabularyValueRegistered(
  app: string,
  namespace: string,
  value: string,
): boolean {
  return vocabularyById.has(vocabularyEntryId(app, namespace, value));
}

/**
 * Validates Factoid category slots for an app against the registry.
 * @param app Owning application (must have a slot map)
 * @param category1 Slot-1 value
 * @param category2 Slot-2 value
 * @throws Error when the app has no slot map or a value is unregistered
 */
export function assertValidFactoidCategories(
  app: string,
  category1: string,
  category2: string,
): void {
  const slotMap = VOCABULARY_APP_SLOT_MAPS[app];
  if (!slotMap) {
    throw new Error(`No vocabulary slot map registered for app '${app}'`);
  }
  if (!isVocabularyValueRegistered(app, slotMap.category1, category1)) {
    throw new Error(`Unknown ${slotMap.category1} value '${category1}' for app '${app}'`);
  }
  if (!isVocabularyValueRegistered(app, slotMap.category2, category2)) {
    throw new Error(`Unknown ${slotMap.category2} value '${category2}' for app '${app}'`);
  }
}

/**
 * Returns all registered vocabulary entries (for tests / diagnostics).
 * @returns Snapshot of registered entries
 */
export function getAllVocabularyEntries(): readonly VocabularyEntry[] {
  return vocabularyEntries;
}

/**
 * Returns app ids that have a slot map and/or seeded vocabulary entries.
 * @returns Sorted unique app ids suitable for FactoidGrid app filter options
 */
export function listVocabularyApps(): string[] {
  const apps = new Set<string>(Object.keys(VOCABULARY_APP_SLOT_MAPS));
  for (const entry of vocabularyEntries) {
    apps.add(entry.app);
  }
  return [...apps].sort();
}
